import React from "react";

import Paragraph from "@common/components/Paragraph/Paragraph";
import { Spinner } from "@common/components/Spinner/Spinner";
import { TimeGroup } from "@common/models/Analytics.model";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  TooltipItem,
} from "chart.js";
import { format } from "date-fns";
import { Bar } from "react-chartjs-2";

import { nFormatter } from "../../helpers/analyticsHelper";

ChartJS.register(CategoryScale, LinearScale, PointElement, BarElement, Title, Tooltip, Legend, Filler);

export interface StackedBarValue {
  label: string;
  value: number;
}

export interface StackedBarEntry {
  time: string;
  values: StackedBarValue[];
}

interface StackedBarChartProps {
  style?: React.CSSProperties;
  data: StackedBarEntry[];
  loading?: boolean;
  error?: boolean;
  colors?: string[];
}

const DEFAULT_COLORS = ["#b77dff", "#d4b1ff", "#e5d0ff", "#efe3ff", "#fbf5ff"];

export const StackedBarChart = ({ style, data, loading, error, colors = DEFAULT_COLORS }: StackedBarChartProps) => {
  const sortedData = [...data].sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

  const stackedTotals = sortedData.map((entry) => entry.values.reduce((sum, v) => sum + v.value, 0));
  const chartMax = stackedTotals.length ? Math.max(...stackedTotals) : 10;
  const max = chartMax && chartMax !== 0 ? chartMax : 10;
  const stepSize = Math.round(max / 2);

  const labels = sortedData.map((entry) => entry.time);

  const datasetLabels = sortedData[0]?.values.map((v) => v.label) ?? [];

  const chartData: ChartData<"bar"> = {
    labels,
    datasets: datasetLabels.map((label, i) => ({
      label,
      data: sortedData.map((entry) => entry.values[i]?.value ?? 0),
      backgroundColor: colors[(datasetLabels.length - 1 - i) % colors.length],
      borderRadius: 6,
      barThickness: 32,
      borderWidth: { top: 1, bottom: 0, left: 0, right: 0 },
      borderColor: "#8a63ba",
    })),
  };

  if (loading)
    return (
      <div style={{ height: "19.8rem" }}>
        <Spinner />
      </div>
    );

  if (error) {
    return (
      <Flex
        style={{ height: "24.6rem" }}
        $justifyContent="center"
        $alignItems="center"
      >
        <Paragraph>{"Error presenting data for this period"}</Paragraph>
      </Flex>
    );
  }

  if (!data.length) {
    return (
      <Flex
        style={{ height: "24.6rem" }}
        $justifyContent="center"
        $alignItems="center"
      >
        <Paragraph>{"No data available for this period"}</Paragraph>
      </Flex>
    );
  }

  return (
    <div style={style}>
      <Bar
        options={getChartOptions(max, stepSize)}
        data={chartData}
      />
    </div>
  );
};

const getChartOptions = (max: number, stepSize: number) => {
  return {
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      intersect: true,
      mode: "point",
    },
    scales: {
      y: {
        stacked: true,
        afterDataLimits: (scale) => {
          scale.max = max;
          scale.min = 0;
        },
        display: true,
        ticks: {
          color: vars.colors.grey60,
          stepSize: stepSize,
          font: {
            family: vars.fonts.fontFamily,
            size: 12,
            weight: 500,
          },
          callback: function (label) {
            return String(nFormatter(Number(label))).toLowerCase();
          },
        },
        position: "right" as const,
        border: { dash: [4, 4], display: false },
        grid: {
          color: vars.colors.grey90,
          tickColor: "transparent",
          tickBorderDash: [0, 1],
          tickLength: 10,
          tickWidth: 1,
          drawTicks: true,
          drawOnChartArea: true,
        },
      },
      x: {
        stacked: true,
        padding: 0,
        ticks: {
          display: true,
          color: vars.colors.grey60,
          font: {
            family: vars.fonts.fontFamily,
            size: 12,
            weight: 500,
          },
          callback: function (_value, index) {
            const label = this.getLabelForValue(index);
            return formatXLabel(label, TimeGroup.MONTH);
          },
        },
        border: { display: true, color: vars.colors.grey80 },
        grid: {
          display: false,
        },
      },
    },

    plugins: {
      legend: {
        display: false,
        position: "top" as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        usePointStyle: true,
        backgroundColor: "rgba(44, 44, 46, 0.9)",
        borderColor: vars.colors.grey90,
        borderWidth: 1,
        boxPadding: 4,
        boxWidth: 6,
        boxHeight: 6,
        titleColor: vars.colors.white,
        titleMarginBottom: 4,
        titleFont: {
          family: vars.fonts.fontFamily,
          size: 14,
          weight: 500,
        },
        bodyColor: vars.colors.grey40,
        bodyFont: {
          family: vars.fonts.fontFamily,
          size: 14,
          weight: 500,
        },
        callbacks: {
          title: (xDatapoint: TooltipItem<"bar">[]) => {
            const { dataIndex, chart } = xDatapoint[0];
            const total = chart.data.datasets.reduce((sum, ds) => sum + Number(ds.data[dataIndex] ?? 0), 0);
            return `${format(new Date(xDatapoint[0].label), "MMMM yyyy")}: ${nFormatter(total)}`;
          },
          label: (yDatapoint: TooltipItem<"bar">) => {
            return `${nFormatter(Number(yDatapoint.raw))} ${yDatapoint.dataset.label}`;
          },
        },
      },
    },
    onHover: (event, chartElements) => {
      if (!event.native?.target) return;

      const el = event.native.target as HTMLElement;
      if (chartElements.length) {
        el.style.cursor = "help";
      } else {
        el.style.cursor = "default";
      }
    },
  } as ChartOptions<"bar">;
};

const formatXLabel = (isoString: string, group: TimeGroup): string => {
  const date = new Date(isoString);
  switch (group) {
    case TimeGroup.MONTH:
      return date.toLocaleString("en-US", { month: "short" });
    case TimeGroup.WEEK:
    case TimeGroup.DAY:
      return date.toLocaleString("en-US", { month: "short", day: "numeric" });
  }
};
